import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Check, Shield, Sparkles, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useSubscription } from '@/hooks/useSubscription';
import { StripeEmbeddedCheckout } from '@/components/StripeEmbeddedCheckout';
import { PaymentTestModeBanner } from '@/components/PaymentTestModeBanner';
import { createPortalSession } from '@/utils/payments.functions';
import { getStripeEnvironment } from '@/lib/stripe';
import { Footer } from '@/components/Footer';

export const Route = createFileRoute('/pricing')({
  head: () => ({
    meta: [
      { title: 'Pricing — LendyTrack' },
      { name: 'description', content: 'Upgrade to LendyTrack Premium for unlimited AI coaching, full loan breakdowns, and saved analyses.' },
      { property: 'og:title', content: 'Pricing — LendyTrack' },
      { property: 'og:description', content: 'Plans for free and premium tiers — unlimited coaching, loan analyses, and lender intelligence.' },
      { property: 'og:url', content: '/pricing' },
      { property: 'og:image', content: '/og-image.jpg' },
      { name: 'twitter:image', content: '/og-image.jpg' },
    ],
    links: [{ rel: 'canonical', href: '/pricing' }],
  }),
  component: PricingPage,
});

const FEATURES = [
  { label: 'Unlimited AI Coach messages', free: false, free_note: '3 messages / day' },
  { label: 'Full Loan Analyzer breakdown + recommendations', free: false },
  { label: 'All lender risk profiles & deep-dives', free: false, free_note: 'Top 5 only' },
  { label: 'Personal dashboard + saved analyses & alerts', free: false },
  { label: 'Lender search & basic comparisons', free: true },
  { label: 'APR red-flag detection', free: true },
];

function PricingPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { isPremium, subscription } = useSubscription();
  const [priceId, setPriceId] = useState<string | null>(null);
  const [interval, setInterval] = useState<'monthly' | 'yearly'>('monthly');
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    // close checkout if user becomes premium
    if (isPremium) setPriceId(null);
  }, [isPremium]);

  const handleUpgrade = (id: 'premium_monthly' | 'premium_yearly') => {
    if (!user) {
      navigate({ to: '/auth', search: { redirect: '/pricing' } });
      return;
    }
    setInterval(id === 'premium_yearly' ? 'yearly' : 'monthly');
    setPriceId(id);
  };

  const openPortal = async () => {
    setPortalLoading(true);
    const result = await createPortalSession({
      data: { environment: getStripeEnvironment(), returnUrl: window.location.href },
    });
    setPortalLoading(false);
    if ('error' in result) {
      alert(result.error);
      return;
    }
    window.open(result.url, '_blank');
  };

  return (
    <div className="min-h-screen bg-gradient-surface">
      <PaymentTestModeBanner />
      <header className="border-b border-border bg-card/70 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-2">
            <div className="grid h-9 w-9 place-items-center rounded-lg bg-gradient-hero text-primary-foreground shadow-soft">
              <Shield className="h-5 w-5" />
            </div>
            <span className="font-display text-lg font-semibold">LendyTrack</span>
          </Link>
          <div className="flex items-center gap-3 text-sm">
            <Link to="/dashboard" className="text-muted-foreground hover:text-foreground">Dashboard</Link>
            {!authLoading && !user && (
              <Link to="/auth" className="rounded-md bg-primary px-3 py-1.5 font-semibold text-primary-foreground">Sign in</Link>
            )}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-14">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-accent/15 px-3 py-1 text-xs font-medium uppercase tracking-widest text-accent">
            <Sparkles className="h-3 w-3" /> Plans
          </div>
          <h1 className="mt-4 font-display text-4xl font-semibold tracking-tight">Borrow smarter for less than a coffee.</h1>
          <p className="mt-3 text-sm text-muted-foreground">No ads. No data sold. Cancel anytime.</p>
        </div>

        {priceId ? (
          <div className="mx-auto mt-10 max-w-2xl rounded-2xl border border-border bg-card p-6 shadow-soft">
            <div className="mb-4 flex items-center justify-between">
              <div className="font-display text-lg font-semibold">
                Subscribe — Premium ({interval})
              </div>
              <button onClick={() => setPriceId(null)} className="text-sm text-muted-foreground hover:text-foreground" aria-label="Close">
                <X className="h-5 w-5" />
              </button>
            </div>
            <StripeEmbeddedCheckout
              priceId={priceId}
              returnUrl={`${window.location.origin}/checkout/return?session_id={CHECKOUT_SESSION_ID}`}
            />
          </div>
        ) : (
          <div className="mt-10 grid gap-6 md:grid-cols-2">
            <PlanCard
              name="Free"
              price="$0"
              cadence="forever"
              ctaLabel={user ? 'Current plan' : 'Get started'}
              ctaDisabled={!!user && !isPremium}
              ctaTo={user ? undefined : '/auth'}
              highlight={false}
              features={FEATURES.map((f) => ({
                label: f.free_note ? `${f.label} (${f.free_note})` : f.label,
                included: f.free,
              }))}
            />
            <PlanCard
              name="Premium"
              price="$9.99"
              cadence="per month · or $79/yr (save 34%)"
              highlight
              ctaLabel={isPremium ? 'Manage subscription' : 'Upgrade — Monthly'}
              ctaSecondaryLabel={isPremium ? undefined : 'Upgrade — Yearly ($79)'}
              ctaDisabled={portalLoading}
              onCta={() => (isPremium ? openPortal() : handleUpgrade('premium_monthly'))}
              onCtaSecondary={() => handleUpgrade('premium_yearly')}
              features={FEATURES.map((f) => ({ label: f.label, included: true }))}
              statusNote={
                isPremium
                  ? subscription?.cancel_at_period_end
                    ? `Access until ${subscription.current_period_end ? new Date(subscription.current_period_end).toLocaleDateString() : 'period end'}`
                    : 'Active — thank you!'
                  : undefined
              }
            />
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}

function PlanCard(props: {
  name: string;
  price: string;
  cadence: string;
  features: { label: string; included: boolean }[];
  highlight?: boolean;
  ctaLabel: string;
  ctaSecondaryLabel?: string;
  ctaDisabled?: boolean;
  ctaTo?: string;
  onCta?: () => void;
  onCtaSecondary?: () => void;
  statusNote?: string;
}) {
  return (
    <div
      className={`rounded-2xl border p-8 shadow-soft ${
        props.highlight ? 'border-accent bg-gradient-hero text-primary-foreground' : 'border-border bg-card'
      }`}
    >
      <div className="flex items-baseline justify-between">
        <h2 className="font-display text-2xl font-semibold">{props.name}</h2>
        {props.highlight && (
          <span className="rounded-full bg-accent px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-primary">
            Best value
          </span>
        )}
      </div>
      <div className="mt-3">
        <span className="font-display text-4xl font-bold">{props.price}</span>
        <span className={`ml-2 text-sm ${props.highlight ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
          {props.cadence}
        </span>
      </div>
      {props.statusNote && (
        <div className={`mt-2 text-xs ${props.highlight ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>
          {props.statusNote}
        </div>
      )}
      <ul className="mt-6 space-y-2">
        {props.features.map((f) => (
          <li key={f.label} className="flex items-start gap-2 text-sm">
            {f.included ? (
              <Check className={`mt-0.5 h-4 w-4 ${props.highlight ? 'text-accent' : 'text-success'}`} />
            ) : (
              <X className={`mt-0.5 h-4 w-4 ${props.highlight ? 'text-primary-foreground/40' : 'text-muted-foreground/50'}`} />
            )}
            <span className={f.included ? '' : props.highlight ? 'text-primary-foreground/50 line-through' : 'text-muted-foreground line-through'}>
              {f.label}
            </span>
          </li>
        ))}
      </ul>
      <div className="mt-6 space-y-2">
        {props.ctaTo ? (
          <Link
            to={props.ctaTo}
            className={`block w-full rounded-md px-4 py-2.5 text-center text-sm font-semibold ${
              props.highlight ? 'bg-accent text-primary' : 'bg-primary text-primary-foreground'
            } hover:opacity-90`}
          >
            {props.ctaLabel}
          </Link>
        ) : (
          <button
            onClick={props.onCta}
            disabled={props.ctaDisabled}
            className={`block w-full rounded-md px-4 py-2.5 text-center text-sm font-semibold disabled:opacity-50 ${
              props.highlight ? 'bg-accent text-primary' : 'bg-primary text-primary-foreground'
            } hover:opacity-90`}
          >
            {props.ctaLabel}
          </button>
        )}
        {props.ctaSecondaryLabel && (
          <button
            onClick={props.onCtaSecondary}
            className={`block w-full rounded-md border px-4 py-2 text-center text-sm font-semibold ${
              props.highlight
                ? 'border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10'
                : 'border-border hover:bg-muted'
            }`}
          >
            {props.ctaSecondaryLabel}
          </button>
        )}
      </div>
    </div>
  );
}
